import React, { useState, useEffect } from 'react';
import { adminApi } from '../api/adminApi';
import toast, { Toaster } from 'react-hot-toast';
import PremiumTable from '../components/common/PremiumTable';
import PageHeader from '../components/common/PageHeader';
import { useNavigate } from 'react-router-dom';
import { PencilSquareIcon, NoSymbolIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const Businesses = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const res = await adminApi.getAllBusinesses();
      setBusinesses(res.data.data);
    } catch (error) {
      console.error('Error fetching businesses:', error);
      toast.error('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBlock = async (businessId, currentStatus) => {
    try {
      await adminApi.updateBusiness(businessId, { isActive: !currentStatus });
      toast.success(`Business ${currentStatus ? 'suspended' : 'activated'} successfully`);
      fetchBusinesses(); // refresh
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update business status');
    }
  };

  const columns = [
    {
      header: 'Business Name',
      accessor: 'name',
      render: (row) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{row.name}</div>
          <div className="text-xs text-gray-500">GST: {row.gstNumber || 'N/A'}</div>
        </div>
      )
    },
    {
      header: 'Type',
      accessor: 'type',
      render: (row) => (
        <span className="text-sm text-gray-900 capitalize">{row.type || 'N/A'}</span>
      )
    },
    {
      header: 'Owner Info',
      accessor: 'ownerId.name',
      render: (row) => (
        row.ownerId ? (
          <>
            <div className="text-sm text-gray-900">{row.ownerId.name}</div>
            <div className="text-xs text-gray-500">{row.ownerId.email}</div>
          </>
        ) : (
          <div className="text-sm text-gray-500">Unknown</div>
        )
      )
    },
    {
      header: 'Created At',
      accessor: 'createdAt',
      render: (row) => (
        <span className="text-sm text-gray-500">
          {new Date(row.createdAt).toLocaleDateString()}
        </span>
      )
    },
    {
      header: 'Status',
      accessor: 'isActive',
      render: (row) => (
        row.isActive 
          ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Active</span>
          : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">Suspended</span>
      )
    },
    {
      header: 'Actions',
      render: (row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigate(`/businesses/edit/${row._id}`, { state: { business: row } })}
            className="flex items-center px-3 py-1.5 bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200 rounded-full text-xs font-bold transition-all shadow-sm hover:shadow"
          >
            <PencilSquareIcon className="w-3.5 h-3.5 mr-1.5 text-gray-500" />
            Edit
          </button>
          <button
            onClick={() => handleToggleBlock(row._id, row.isActive)}
            className={`flex items-center px-3 py-1.5 border rounded-full text-xs font-bold transition-all shadow-sm hover:shadow ${
              row.isActive 
                ? 'bg-red-50 text-red-700 border-red-100 hover:bg-red-100' 
                : 'bg-green-50 text-green-700 border-green-100 hover:bg-green-100'
            }`}
          >
            {row.isActive ? (
              <>
                <NoSymbolIcon className="w-3.5 h-3.5 mr-1.5 text-red-500" />
                Block
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-3.5 h-3.5 mr-1.5 text-green-500" />
                Unblock
              </>
            )}
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-transparent pb-12">
      <Toaster position="top-right" />
      <PageHeader 
        title="Platform Businesses"
        subtitle="Manage and oversee all businesses registered on the platform."
      />
      <main className="max-w-7xl">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading businesses...</div>
        ) : (
          <PremiumTable 
            data={businesses}
            columns={columns}
            searchKeys={['name', 'gstNumber', 'type', 'ownerId.name', 'ownerId.email']}
            primaryKey="_id"
            paginationMode="standard"
            itemsPerPage={10}
          />
        )}
      </main>
    </div>
  );
};

export default Businesses;
